/**
 * The board reducer.
 *
 * One pure function from `(state, action)` to `state`, holding every rule about
 * how the board changes. The Durable Object has an equivalent module
 * (`apps/api/src/durable/mutations.ts`) and the two are deliberately not shared:
 * the server's job is to decide what is *true*, and the client's is to decide
 * what is *shown*. They converge because they consume the same events, not
 * because they run the same code.
 *
 * ## Two entry points, one rule set
 *
 * `applyBoardEvent` handles a persisted `BoardEvent` - the shape that arrives in
 * a `welcome` replay, from the `?since=` delta endpoint, and on the generic
 * `{ t: 'event' }` frame. `applyServerMessage` handles the typed frames, which
 * carry a hydrated entity rather than a row. Both funnel into the same upsert
 * helpers, so a card that arrives via a live `card.moved` frame and the same card
 * arriving via a replay produce byte-identical state. That is the property that
 * makes reconnection correct rather than merely plausible.
 *
 * ## Sequence handling
 *
 * `sequence` never moves backwards. A frame that carries a lower sequence than
 * the client has already applied is applied anyway - it may still carry
 * information - but it does not lower the watermark, because the watermark is
 * what the next `hello` sends as `since` and a watermark that regressed would
 * cause the server to replay events the client already has.
 *
 * ## Payload validation
 *
 * Frames arrive as `unknown`-typed payloads. `z.custom<unknown>()` in the
 * protocol schema is deliberate: validating a full `Card` with Zod on every
 * frame would cost more than it buys, and the server is the only writer. But
 * "the server is the only writer" is not "the server is the only writer of this
 * version", so the parsers below build a `Card` field by field rather than
 * casting. A field that is missing or the wrong type degrades to a sensible
 * default instead of propagating `undefined` into a component that then renders
 * `undefined.title` - and a build of the client that is one deploy behind the
 * server still renders.
 */

import type {
  Board,
  BoardEvent,
  Card,
  CardAttachment,
  CardComment,
  Column,
  ConnectionStatus,
  Label,
  Presence,
  Priority,
  ServerMessage,
} from '@slate/shared';
import { PRIORITIES, sortByPosition } from '@slate/shared';
import type { Notice, PendingOp, BoardState, TypingEntry } from './types.js';
import { initialBoardState } from './types.js';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type BoardAction =
  | { type: 'reset'; boardId: string | null }
  | { type: 'loading' }
  | { type: 'failed'; message: string; terminal: boolean }
  | {
      type: 'snapshot';
      board: Board;
      columns: Column[];
      cards: Card[];
      labels: Label[];
      members: BoardState['members'];
      sequence: number;
    }
  | { type: 'events'; events: BoardEvent[]; sequence: number | null }
  | { type: 'server-message'; message: ServerMessage }
  | { type: 'optimistic'; op: PendingOp }
  | { type: 'sent'; opId: string; revision: number }
  | { type: 'retire'; opId: string }
  | { type: 'settle'; opId: string; card?: unknown; comment?: unknown; column?: unknown }
  | { type: 'rejected'; opId: string | null; message: string; current: unknown }
  | { type: 'connection'; status: ConnectionStatus }
  | { type: 'latency'; ms: number }
  | {
      type: 'card-detail';
      cardId: string;
      card: Card;
      comments: CardComment[];
      attachments: CardAttachment[];
      history: BoardEvent[];
    }
  | { type: 'comments'; cardId: string; comments: CardComment[] }
  | { type: 'attachments'; cardId: string; attachments: CardAttachment[] }
  /** Upsert a column from an HTTP response. Used by the rename path, which has no
   * socket frame of its own and therefore no broadcast to consume. */
  | { type: 'column-upsert'; column: unknown }
  | { type: 'activity-page'; events: BoardEvent[]; nextCursor: string | null; append: boolean }
  | { type: 'activity-stale'; stale: boolean }
  | { type: 'resync-needed'; needed: boolean }
  | { type: 'notice'; notice: Notice }
  | { type: 'dismiss-notice'; id: string };

// ---------------------------------------------------------------------------
// Payload parsers
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asPriority(value: unknown): Priority {
  if (typeof value === 'string' && (PRIORITIES as readonly string[]).includes(value)) {
    return value as Priority;
  }
  return 'none';
}

function asLabel(value: unknown): Label | null {
  if (!isRecord(value)) return null;
  const id = asString(value['id']);
  const name = asString(value['name']);
  const colour = asString(value['colour']);
  if (id === null || name === null || colour === null) return null;
  return { id, boardId: asString(value['boardId']) ?? '', name, colour };
}

function asCard(value: unknown): Card | null {
  if (!isRecord(value)) return null;

  const id = asString(value['id']);
  const boardId = asString(value['boardId']);
  const columnId = asString(value['columnId']);
  const title = asString(value['title']);
  const position = asNumber(value['position']);
  const version = asNumber(value['version']);

  // These six are the fields a card cannot be rendered or written without. A
  // payload missing one of them is not a card, and guessing would produce a row
  // that looks plausible and behaves wrongly - a version of 0, for instance,
  // makes every subsequent conditional write fail.
  if (id === null || boardId === null || columnId === null || title === null) return null;
  if (position === null || version === null) return null;

  const labels = Array.isArray(value['labels']) ? value['labels'] : [];
  const now = new Date().toISOString();

  return {
    id,
    boardId,
    columnId,
    title,
    description: asString(value['description']),
    assigneeId: asString(value['assigneeId']),
    dueDate: asString(value['dueDate']),
    priority: asPriority(value['priority']),
    position,
    version,
    archivedAt: asString(value['archivedAt']),
    createdBy: asString(value['createdBy']) ?? '',
    createdAt: asString(value['createdAt']) ?? now,
    updatedAt: asString(value['updatedAt']) ?? now,
    labels: labels.map(asLabel).filter((label): label is Label => label !== null),
  };
}

function asColumn(value: unknown): Column | null {
  if (!isRecord(value)) return null;
  const id = asString(value['id']);
  const boardId = asString(value['boardId']);
  const name = asString(value['name']);
  const position = asNumber(value['position']);
  if (id === null || boardId === null || name === null || position === null) return null;
  const now = new Date().toISOString();
  return {
    id,
    boardId,
    name,
    position,
    createdAt: asString(value['createdAt']) ?? now,
    updatedAt: asString(value['updatedAt']) ?? now,
  };
}

function asComment(value: unknown): CardComment | null {
  if (!isRecord(value)) return null;
  const id = asString(value['id']);
  const cardId = asString(value['cardId']);
  const boardId = asString(value['boardId']);
  const body = asString(value['body']);
  const createdAt = asString(value['createdAt']);
  const author = value['author'];
  if (id === null || cardId === null || boardId === null || body === null || createdAt === null) return null;
  if (!isRecord(author)) return null;

  const authorId = asString(author['id']);
  const authorName = asString(author['name']);
  const authorColour = asString(author['colour']);
  if (authorId === null || authorName === null || authorColour === null) return null;

  return {
    id,
    cardId,
    boardId,
    author: { id: authorId, name: authorName, colour: authorColour, avatarUrl: asString(author['avatarUrl']) },
    body,
    editedAt: asString(value['editedAt']),
    deletedAt: asString(value['deletedAt']),
    createdAt,
  };
}

function asAttachment(value: unknown): CardAttachment | null {
  if (!isRecord(value)) return null;
  const id = asString(value['id']);
  const cardId = asString(value['cardId']);
  const boardId = asString(value['boardId']);
  const assetKey = asString(value['assetKey']);
  const filename = asString(value['filename']);
  const contentType = asString(value['contentType']);
  const byteSize = asNumber(value['byteSize']);
  const createdAt = asString(value['createdAt']);
  if (
    id === null ||
    cardId === null ||
    boardId === null ||
    assetKey === null ||
    filename === null ||
    contentType === null ||
    byteSize === null ||
    createdAt === null
  ) {
    return null;
  }
  return {
    id,
    cardId,
    boardId,
    uploaderId: asString(value['uploaderId']) ?? '',
    assetKey,
    filename,
    contentType,
    byteSize,
    createdAt,
  };
}

function asPresence(value: unknown): Presence | null {
  if (!isRecord(value)) return null;
  const clientId = asString(value['clientId']);
  const userId = asString(value['userId']);
  const name = asString(value['name']);
  const colour = asString(value['colour']);
  if (clientId === null || userId === null || name === null || colour === null) return null;

  const cursor = value['cursor'];
  return {
    clientId,
    userId,
    name,
    colour,
    cursor:
      isRecord(cursor) && asNumber(cursor['x']) !== null && asNumber(cursor['y']) !== null
        ? { x: asNumber(cursor['x']) ?? 0, y: asNumber(cursor['y']) ?? 0 }
        : null,
    lastSeenAt: asNumber(value['lastSeenAt']) ?? Date.now(),
  };
}

// ---------------------------------------------------------------------------
// Upsert helpers
// ---------------------------------------------------------------------------

/** Replace a card. Never merges: the server's card is authoritative and complete,
 * and merging a partial frame into a full one is how a field that the server
 * cleared stays set on the client. */
function upsertCard(state: BoardState, card: Card): BoardState {
  if (state.cards[card.id] === card) return state;
  return { ...state, cards: { ...state.cards, [card.id]: card } };
}

function removeCard(state: BoardState, cardId: string): BoardState {
  if (state.cards[cardId] === undefined) return state;
  const next = { ...state.cards };
  delete next[cardId];
  return { ...state, cards: next };
}

function upsertColumn(state: BoardState, column: Column): BoardState {
  const index = state.columns.findIndex((existing) => existing.id === column.id);
  const columns = index === -1 ? [...state.columns, column] : state.columns.map((c) => (c.id === column.id ? column : c));
  return { ...state, columns: sortByPosition(columns) };
}

function removeColumn(state: BoardState, columnId: string): BoardState {
  return { ...state, columns: state.columns.filter((column) => column.id !== columnId) };
}

/** Apply a set of `{ id, position }` pairs. Used by `column.reordered`, which
 * ships every position because a reorder can renumber the whole board. */
function applyPositions(state: BoardState, positions: { id: string; position: number }[]): BoardState {
  const byId = new Map(positions.map((entry) => [entry.id, entry.position]));
  const columns = state.columns.map((column) => {
    const position = byId.get(column.id);
    return position === undefined ? column : { ...column, position };
  });
  return { ...state, columns: sortByPosition(columns) };
}

function upsertComment(state: BoardState, comment: CardComment): BoardState {
  const existing = state.comments[comment.cardId];
  // Only mutate a thread that has been loaded. Creating a one-element thread
  // from a broadcast would make the drawer show a single comment and no
  // indication that the rest exist, which is worse than showing a spinner.
  if (existing === undefined) return state;
  const index = existing.findIndex((entry) => entry.id === comment.id);
  const next = index === -1 ? [...existing, comment] : existing.map((entry) => (entry.id === comment.id ? comment : entry));
  return { ...state, comments: { ...state.comments, [comment.cardId]: next } };
}

function removeComment(state: BoardState, cardId: string, commentId: string): BoardState {
  const existing = state.comments[cardId];
  if (existing === undefined) return state;
  return {
    ...state,
    comments: { ...state.comments, [cardId]: existing.filter((entry) => entry.id !== commentId) },
  };
}

function upsertAttachment(state: BoardState, attachment: CardAttachment): BoardState {
  const existing = state.attachments[attachment.cardId];
  if (existing === undefined) return state;
  const index = existing.findIndex((entry) => entry.id === attachment.id);
  const next =
    index === -1 ? [...existing, attachment] : existing.map((entry) => (entry.id === attachment.id ? attachment : entry));
  return { ...state, attachments: { ...state.attachments, [attachment.cardId]: next } };
}

/** Retire a pending operation by id. */
function retirePending(state: BoardState, opId: string): BoardState {
  const pending = acknowledgePending(state.pending, opId);
  return pending === state.pending ? state : { ...state, pending };
}

// ---------------------------------------------------------------------------
// Pending-list operations, shared with the provider
// ---------------------------------------------------------------------------

/**
 * Add an operation, or merge it into the one already in flight for the same
 * card.
 *
 * Exported because `BoardProvider` maintains a synchronous mirror of the pending
 * list - a React ref updated during the dispatch call rather than after a render
 * - and it has to merge identically. Sharing the function is the only way to
 * guarantee the mirror and the state agree; two implementations would drift, and
 * the drift would show up as an edit that silently stops being sent.
 *
 * The merge itself is the reason this exists. Two `card.update` frames for one
 * card would both carry the same `expectedVersion`, because the base version does
 * not advance until the first is acknowledged - so the second would be rejected
 * as stale and the user's edit would be lost for a reason unrelated to what they
 * did.
 */
export function mergePending(pending: readonly PendingOp[], op: PendingOp): PendingOp[] {
  if (op.kind === 'card.update') {
    const index = pending.findIndex((existing) => existing.opId === op.opId);
    const existing = index === -1 ? undefined : pending[index];
    if (existing !== undefined && existing.kind === 'card.update') {
      const merged: PendingOp = {
        ...existing,
        patch: { ...existing.patch, ...op.patch },
        revision: existing.revision + 1,
        at: op.at,
      };
      const next = [...pending];
      next[index] = merged;
      return next;
    }
  }
  return [...pending, op];
}

/** Record which revision a frame carried. See `PendingOp` in types.ts. */
export function markSent(pending: readonly PendingOp[], opId: string, revision: number): PendingOp[] {
  return pending.map((op) =>
    op.opId === opId && op.kind === 'card.update' ? { ...op, sentRevision: revision } : op,
  );
}

/**
 * Retire an operation because the server acknowledged it - and *only* if the
 * acknowledgement covers every local edit.
 *
 * This is the whole reason `sentRevision` exists. A user typing in a title field
 * produces a merge into the pending operation while the first frame is still in
 * flight. When the acknowledgement arrives it refers to the revision that was
 * sent, not to the revision now in the list. Retiring unconditionally would throw
 * away the keystrokes typed during the round trip, and the field would visibly
 * snap back - the single most common bug in optimistic text editing.
 *
 * A non-`card.update` operation has no revisions and is retired immediately.
 */
export function acknowledgePending(pending: readonly PendingOp[], opId: string): PendingOp[] {
  const op = pending.find((entry) => entry.opId === opId);
  if (op === undefined) return pending as PendingOp[];
  if (op.kind === 'card.update' && op.revision !== op.sentRevision) return pending as PendingOp[];
  return pending.filter((entry) => entry.opId !== opId);
}

/** Drop an operation unconditionally. Used when it is definitively over: settled
 * from an HTTP response, or rejected. */
export function dropPending(pending: readonly PendingOp[], opId: string): PendingOp[] {
  return pending.filter((op) => op.opId !== opId);
}

/** The `opId` a server frame acknowledges, if any. Used by the provider to keep
 * its synchronous mirror in step without duplicating the reducer's switch. */
export function acknowledgedOpId(message: ServerMessage): string | null {
  switch (message.t) {
    case 'card.created':
    case 'card.moved':
    case 'card.updated':
    case 'card.archived':
    case 'column.created':
    case 'column.reordered':
    case 'comment.created':
      return message.d.opId ?? null;
    case 'error':
      return message.d.opId ?? null;
    default:
      return null;
  }
}

/** Push a notice, capped at four. A board that produces a burst of failures
 * should not fill the screen with them; the oldest is the least useful. */
function withNotice(state: BoardState, notice: Notice): BoardState {
  return { ...state, notices: [...state.notices, notice].slice(-4) };
}

let noticeCounter = 0;
export function makeNotice(tone: Notice['tone'], message: string, requestId?: string | null): Notice {
  noticeCounter += 1;
  return { id: `n${noticeCounter}-${Date.now()}`, tone, message, requestId: requestId ?? null };
}

// ---------------------------------------------------------------------------
// Event application
// ---------------------------------------------------------------------------

/**
 * Apply one persisted event.
 *
 * The `event` frame and the `welcome` replay both land here, which is the point
 * of `board_events` and the broadcast being the same shape. Every branch is
 * written to be safe on a missing payload: a row written by an older deploy, or
 * an event type this build does not know about, must degrade to "the activity
 * feed shows it and the board catches up on the next snapshot" rather than to a
 * thrown exception inside a reducer.
 */
export function applyBoardEvent(state: BoardState, event: BoardEvent): BoardState {
  const sequence = Math.max(state.sequence, event.sequence);
  const payload = event.payload;

  switch (event.type) {
    case 'card.created':
    case 'card.moved':
    case 'card.updated':
    case 'card.archived': {
      const card = asCard(payload['card']);
      if (card === null) return { ...state, sequence };
      return { ...upsertCard(state, card), sequence };
    }
    case 'card.deleted': {
      const cardId = asString(payload['cardId']) ?? asString(payload['id']) ?? event.cardId;
      if (cardId === null) return { ...state, sequence };
      return { ...removeCard(state, cardId), sequence };
    }
    case 'column.created':
    case 'column.updated': {
      const column = asColumn(payload['column']);
      if (column === null) return { ...state, sequence };
      return { ...upsertColumn(state, column), sequence };
    }
    case 'column.deleted': {
      const columnId = asString(payload['columnId']) ?? asString(payload['id']);
      if (columnId === null) return { ...state, sequence };
      return { ...removeColumn(state, columnId), sequence };
    }
    case 'column.reordered': {
      const raw = payload['positions'];
      if (!Array.isArray(raw)) return { ...state, sequence };
      const positions: { id: string; position: number }[] = [];
      for (const entry of raw) {
        if (!isRecord(entry)) continue;
        const id = asString(entry['id']);
        const position = asNumber(entry['position']);
        if (id !== null && position !== null) positions.push({ id, position });
      }
      return { ...applyPositions(state, positions), sequence };
    }
    case 'comment.created':
    case 'comment.updated': {
      const comment = asComment(payload['comment']);
      if (comment === null) return { ...state, sequence };
      return { ...upsertComment(state, comment), sequence };
    }
    case 'comment.deleted': {
      const comment = asComment(payload['comment']);
      if (comment === null) return { ...state, sequence };
      return { ...removeComment(state, comment.cardId, comment.id), sequence };
    }
    case 'attachment.created': {
      const attachment = asAttachment(payload['attachment']);
      if (attachment === null) return { ...state, sequence };
      return { ...upsertAttachment(state, attachment), sequence };
    }
    case 'board.created':
    case 'board.updated':
    case 'board.archived': {
      const board = payload['board'];
      if (!isRecord(board)) return { ...state, sequence };
      const name = asString(board['name']);
      if (name === null || state.board === null) return { ...state, sequence };
      return {
        ...state,
        board: {
          ...state.board,
          name,
          description: asString(board['description']),
          archivedAt: asString(board['archivedAt']),
        },
        sequence,
      };
    }
    case 'member.joined':
      // The event carries a user id and a role, not a hydrated member with a
      // name and a colour. Fabricating one would put a blank avatar in the
      // presence stack; the member list is refreshed with the next snapshot
      // instead, and the activity feed still shows the join.
      return { ...state, sequence };
  }
}

// ---------------------------------------------------------------------------
// Server messages
// ---------------------------------------------------------------------------

/** The sequence a frame was produced at, or `null` for frames that are not part
 * of the ordered stream. Presence and typing frames are ephemeral and
 * deliberately unsequenced - see the protocol docblock. */
function sequencedOf(message: ServerMessage): number | null {
  switch (message.t) {
    case 'card.created':
    case 'card.moved':
    case 'card.updated':
    case 'card.archived':
    case 'column.created':
    case 'column.reordered':
    case 'comment.created':
      return message.d.sequence;
    default:
      return null;
  }
}

/**
 * Apply a server frame, and detect a dropped one.
 *
 * The gap check is done once, here, rather than inside each sequenced branch.
 * Putting it at the boundary means a message type added later cannot forget it -
 * the failure mode of forgetting is a client that renders a board which silently
 * disagrees with everyone else's, and that is not a failure worth risking on
 * remembering to copy a line.
 */
export function applyServerMessage(state: BoardState, message: ServerMessage): BoardState {
  const incoming = sequencedOf(message);
  const next = reduceServerMessage(state, message);

  if (incoming !== null && sequenceGap(state.sequence, incoming)) {
    return { ...next, needsResync: true };
  }
  return next;
}

function reduceServerMessage(state: BoardState, message: ServerMessage): BoardState {
  switch (message.t) {
    case 'welcome': {
      const presence: BoardState['presence'] = {};
      for (const entry of message.d.presence) {
        const parsed = asPresence(entry);
        if (parsed !== null) presence[parsed.clientId] = parsed;
      }

      let next: BoardState = {
        ...state,
        clientId: message.d.clientId,
        // `Math.max` rather than assignment: a live frame can be delivered
        // before `welcome` on a socket that was accepted but not yet marked
        // ready, and letting the handshake lower the watermark would make the
        // next reconnect replay events that have already been applied.
        sequence: Math.max(state.sequence, message.d.sequence),
        presence,
        connection: 'connected',
        needsResync: state.needsResync || message.d.resyncRequired,
      };

      // The replay is applied inside the handshake rather than as a burst of
      // separate frames, so a live frame cannot interleave between two replayed
      // ones. See `handleHello` in the Durable Object.
      if (message.d.replay !== null) {
        for (const event of message.d.replay) next = applyBoardEvent(next, event);
      }

      return next;
    }

    case 'presence.join': {
      const presence = asPresence(message.d.presence);
      if (presence === null) return state;
      return { ...state, presence: { ...state.presence, [presence.clientId]: presence } };
    }

    case 'presence.leave': {
      if (state.presence[message.d.clientId] === undefined) return state;
      const presence = { ...state.presence };
      delete presence[message.d.clientId];
      return { ...state, presence };
    }

    case 'presence.cursor': {
      const existing = state.presence[message.d.clientId];
      if (existing === undefined) return state;
      return {
        ...state,
        presence: {
          ...state.presence,
          [message.d.clientId]: { ...existing, cursor: message.d.cursor, lastSeenAt: Date.now() },
        },
      };
    }

    case 'card.created':
    case 'card.moved':
    case 'card.updated':
    case 'card.archived': {
      const card = asCard(message.d.card);
      if (card === null) return state;
      const withCard = upsertCard(state, card);
      const settled = message.d.opId === undefined ? withCard : retirePending(withCard, message.d.opId);
      return {
        ...settled,
        sequence: Math.max(settled.sequence, message.d.sequence),
        activityStale: true,
      };
    }

    case 'column.created': {
      const column = asColumn(message.d.column);
      if (column === null) return state;
      const withColumn = upsertColumn(state, column);
      const settled = message.d.opId === undefined ? withColumn : retirePending(withColumn, message.d.opId);
      return { ...settled, sequence: Math.max(settled.sequence, message.d.sequence), activityStale: true };
    }

    case 'column.reordered': {
      const withPositions = applyPositions(state, message.d.positions);
      const settled = message.d.opId === undefined ? withPositions : retirePending(withPositions, message.d.opId);
      return { ...settled, sequence: Math.max(settled.sequence, message.d.sequence), activityStale: true };
    }

    case 'comment.created': {
      const comment = asComment(message.d.comment);
      if (comment === null) return state;
      const withComment = upsertComment(state, comment);
      const settled = message.d.opId === undefined ? withComment : retirePending(withComment, message.d.opId);
      return { ...settled, sequence: Math.max(settled.sequence, message.d.sequence), activityStale: true };
    }

    case 'typing': {
      const key = message.d.cardId ?? '*';
      const existing = state.typing[key] ?? [];
      const others = existing.filter((entry) => entry.userId !== message.d.userId);

      // A `typing: false` for someone who was never shown as typing is a no-op
      // rather than an empty entry, so the indicator does not flicker on.
      const next = message.d.isTyping ? [...others, { userId: message.d.userId, name: message.d.name, at: Date.now() }] : others;
      return { ...state, typing: { ...state.typing, [key]: next } };
    }

    case 'error': {
      // A rejected frame is scoped to that frame, never to the connection - the
      // client needs the socket to receive the authoritative state that resolves
      // its optimistic entry. `fatal` is the exception, and the connection
      // handles it by closing.
      return reject(state, {
        opId: message.d.opId ?? null,
        message: message.d.message,
        current: message.d.current ?? null,
      });
    }

    case 'pong':
      // Latency is measured by the connection, which owns the clock comparison.
      return state;

    case 'event': {
      const next = applyBoardEvent(state, message.d.event);
      return { ...next, activityStale: true };
    }
  }
}

/**
 * Discard a pending operation and reconcile.
 *
 * The rollback is a *deletion*, not an inverse. That is the payoff of the
 * overlay design in `types.ts`: there is no stored "before" value to restore, so
 * there is no way for the restore to be wrong, and no way for it to clobber a
 * concurrent edit by someone else.
 *
 * `current` is the authoritative row the server attaches to a `version_conflict`.
 * Writing it straight in is what makes the rollback instant - the alternative is
 * a follow-up GET that could itself race with a third writer.
 *
 * When `current` is absent the local state cannot be reconciled from the
 * rejection alone, so `needsResync` is raised and the provider fetches a
 * snapshot. That is the honest failure mode: rather than guessing, the client
 * admits it does not know and asks.
 */
function reject(state: BoardState, args: { opId: string | null; message: string; current: unknown }): BoardState {
  const withoutOp = args.opId === null ? state : retirePending(state, args.opId);

  const card = asCard(args.current);
  if (card !== null) {
    return withNotice(upsertCard(withoutOp, card), makeNotice('error', args.message));
  }

  const column = asColumn(args.current);
  if (column !== null) {
    return withNotice(upsertColumn(withoutOp, column), makeNotice('error', args.message));
  }

  if (args.opId === null) {
    // Not attributable to an optimistic operation - a malformed frame, or a
    // failure the server raised on its own. Surfacing it is enough; there is no
    // local state to correct.
    return withNotice(state, makeNotice('error', args.message));
  }

  return withNotice({ ...withoutOp, needsResync: true }, makeNotice('error', args.message));
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function boardReducer(state: BoardState, action: BoardAction): BoardState {
  switch (action.type) {
    case 'reset':
      return { ...initialBoardState(), boardId: action.boardId };

    case 'loading':
      return { ...state, status: 'loading', error: null };

    case 'failed':
      return { ...state, status: 'error', error: action.message, boardUnavailable: action.terminal };

    case 'snapshot': {
      const cards: Record<string, Card> = {};
      for (const card of action.cards) cards[card.id] = card;

      return {
        ...state,
        status: 'ready',
        error: null,
        board: action.board,
        columns: sortByPosition(action.columns),
        cards,
        labels: action.labels,
        members: action.members,
        // The snapshot's sequence is authoritative for what the client now
        // holds. Pending operations survive a resync: they are still the user's
        // intent, and the overlay re-applies them over the fresh base.
        sequence: Math.max(state.sequence, action.sequence),
        needsResync: false,
      };
    }

    case 'events': {
      let next = state;
      for (const event of action.events) next = applyBoardEvent(next, event);
      if (action.sequence !== null) next = { ...next, sequence: Math.max(next.sequence, action.sequence) };
      return next;
    }

    case 'server-message':
      return applyServerMessage(state, action.message);

    case 'optimistic':
      return { ...state, pending: mergePending(state.pending, action.op) };

    case 'sent':
      return { ...state, pending: markSent(state.pending, action.opId, action.revision) };

    case 'retire':
      return { ...state, pending: dropPending(state.pending, action.opId) };

    /**
     * Resolve an operation from an HTTP response.
     *
     * The socket path retires a pending operation from the broadcast that
     * acknowledges it; the HTTP path has no such frame, so the response body is
     * the acknowledgement. Both end up in the same three upserts, which is why
     * the two transports converge on identical state.
     */
    case 'settle': {
      let next: BoardState = { ...state, pending: dropPending(state.pending, action.opId) };
      const card = asCard(action.card);
      if (card !== null) next = upsertCard(next, card);
      const column = asColumn(action.column);
      if (column !== null) next = upsertColumn(next, column);
      const comment = asComment(action.comment);
      if (comment !== null) next = upsertComment(next, comment);
      return next;
    }

    case 'rejected':
      return reject(state, action);

    case 'connection':
      return { ...state, connection: action.status };

    case 'latency':
      return { ...state, latencyMs: action.ms };

    case 'card-detail':
      return {
        ...state,
        cards: { ...state.cards, [action.cardId]: action.card },
        comments: { ...state.comments, [action.cardId]: action.comments },
        attachments: { ...state.attachments, [action.cardId]: action.attachments },
        history: { ...state.history, [action.cardId]: action.history },
      };

    case 'comments':
      return { ...state, comments: { ...state.comments, [action.cardId]: action.comments } };

    case 'attachments':
      return { ...state, attachments: { ...state.attachments, [action.cardId]: action.attachments } };

    case 'column-upsert': {
      const column = asColumn(action.column);
      return column === null ? state : upsertColumn(state, column);
    }

    case 'activity-page': {
      // Deduplicated by event id. The feed is refetched from the top when a live
      // frame arrives, so a page boundary can legitimately contain rows the
      // client already has - and a duplicate key in a React list is a warning
      // that becomes a rendering bug.
      const seen = new Set(action.append ? state.activity.map((event) => event.id) : []);
      const merged = action.append ? [...state.activity] : [];
      for (const event of action.events) {
        if (seen.has(event.id)) continue;
        seen.add(event.id);
        merged.push(event);
      }
      merged.sort((a, b) => b.sequence - a.sequence);
      return { ...state, activity: merged, activityCursor: action.nextCursor, activityStale: false };
    }

    case 'activity-stale':
      return { ...state, activityStale: action.stale };

    case 'resync-needed':
      return { ...state, needsResync: action.needed };

    case 'notice':
      return withNotice(state, action.notice);

    case 'dismiss-notice':
      return { ...state, notices: state.notices.filter((notice) => notice.id !== action.id) };
  }
}

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Whether a frame skipped events.
 *
 * The board's sequences are dense - the Durable Object derives each one from
 * `MAX(sequence) + 1` - so an incoming sequence more than one ahead of the local
 * watermark means at least one frame was never applied. The socket is ordered,
 * so this is rare, but it is reachable: the Durable Object can be evicted between
 * persisting an event and broadcasting it, and a client that was mid-`welcome`
 * can drop a frame.
 *
 * A gap cannot be repaired by waiting, because the missing frames are gone from
 * the socket. The caller raises `needsResync`, and the provider re-reads board
 * state from its own watermark - which lets the server choose between a small
 * delta and a full snapshot rather than forcing the expensive one.
 */
export function sequenceGap(previous: number, incoming: number): boolean {
  return incoming > previous + 1;
}

/** The current user's presence entry, if this connection has been announced. */
export function selfPresence(state: BoardState): Presence | null {
  if (state.clientId === null) return null;
  return state.presence[state.clientId] ?? null;
}

/** Presence entries other than this connection's own, most recently active
 * first. Two connections for one account are two entries, which is correct: they
 * are two cursors. */
export function otherPresence(state: BoardState): Presence[] {
  return Object.values(state.presence)
    .filter((presence) => presence.clientId !== state.clientId)
    .sort((a, b) => b.lastSeenAt - a.lastSeenAt);
}

/** How many distinct people are viewing, counting connections but not double
 * counting a person who has two tabs open. */
export function viewerCount(state: BoardState): number {
  const userIds = new Set(Object.values(state.presence).map((presence) => presence.userId));
  return userIds.size;
}

/** Typing entries for a card, excluding this user. Stale entries - older than
 * five seconds - are dropped: a client that closes mid-sentence never sends the
 * `typing: false` that would clear them, and an indicator that never clears is
 * worse than no indicator. */
export function typingFor(state: BoardState, cardId: string, selfUserId: string | null): TypingEntry[] {
  const entries = state.typing[cardId] ?? [];
  const cutoff = Date.now() - 5000;
  return entries.filter((entry) => entry.at >= cutoff && entry.userId !== selfUserId);
}

export type { BoardState };
